/* global QuickAddService */

class ProductMappedProductsSwitcher extends HTMLElement {
  constructor() {
    super();
    this.inputs = [];
    this.sectionId = this.getAttribute('data-section-id');
    this.productCache = new Map();
    this.onChange = this.onChange.bind(this);
  }

  connectedCallback() {
    this.inputs = Array.from(this.querySelectorAll('input[name="sibling-product"]'));
    this.attachEvents();

    // Preload siblings when idle
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => this.preloadSiblings());
    } else {
      setTimeout(() => this.preloadSiblings(), 300);
    }
  }

  get isInQuickAddModal() {
    if (this.sectionId?.startsWith('quickadd-')) return true;
    return this.closest('quick-add-modal') !== null;
  }

  get quickAddModal() {
    return this.closest('quick-add-modal');
  }

  disconnectedCallback() {
    this.detachEvents();
  }

  attachEvents() {
    this.inputs.forEach((input) => input.addEventListener('change', this.onChange));
  }

  detachEvents() {
    this.inputs.forEach((input) => input.removeEventListener('change', this.onChange));
  }

  async onChange(event) {
    const target = event.target;

    let currentSizeOptionValue = null;
    const productInfo = this.closest('product-info');
    if (productInfo) {
      const currentSizeOptionPath = 'variant-selects fieldset.option-size input[type="radio"]:checked';
      const currentSizeVariant = productInfo.querySelector(currentSizeOptionPath);
      currentSizeOptionValue = currentSizeVariant?.value || null;
    }

    this.inputs.forEach((input) => {
      input.checked = false;
    });

    target.checked = true;

    if (!target?.value) return;

    const handle = target.value;

    try {
      const newSection = await this.getSectionForHandle(handle);
      if (!newSection) return;

      const newSectionClone = newSection.cloneNode(true);

      const currentSection = document.getElementById(`MainProduct-${this.sectionId}`);
      if (!currentSection) return;

      // Update the URL to the new product
      if (!this.isInQuickAddModal) {
        const url = new URL(window.location.href);
        url.pathname = `/products/${handle}`;
        url.search = '';

        window.history.replaceState({ path: url.toString() }, '', url.toString());
      }

      this.preserveNonReplacable(currentSection, newSectionClone);
      
      // Replace <product-modal> 
      const newProductModal = newSectionClone.querySelector(`#ProductModal-${this.sectionId}`);
      if (newProductModal) {
        const existingProductModal = document.getElementById(`ProductModal-${this.sectionId}`);
        if (existingProductModal) {
          existingProductModal.replaceWith(newProductModal);
        }
      }
      
      currentSection.replaceWith(newSectionClone);

      // Enable zoom on hover
      enableZoomOnHover(2);

      // Persist the selected size option value accross mapped products
      const productInfo = this.closest('product-info');
      if (!productInfo) return;
      const variantSelects = productInfo.querySelector('variant-selects');
      if (!variantSelects) return;

      const previousSizeOptionInput = variantSelects.querySelector('input[value="' + currentSizeOptionValue + '"]');
      if (previousSizeOptionInput) {
        previousSizeOptionInput.checked = true;
        setTimeout(() => {
          const changeEvent = new Event('change');
          previousSizeOptionInput.dispatchEvent(changeEvent);
          variantSelects.dispatchEvent(changeEvent);
        }, 100);
      }
    } catch (e) {
      console.error('[SiblingProductSwitcher] Failed to switch product', e);
    }
  }

  async getSectionForHandle(handle) {
    if (this.productCache.has(handle)) return this.productCache.get(handle);

    // There is an edge case, quick add modal overrides the section id by adding 'quickadd-' prefix, so we need to remove it.
    const sectionId = this.sectionId.replace('quickadd-', '');

    const response = await fetch(`/products/${handle}?section_id=${sectionId}`);
    const htmlText = await response.text();

    // If the section is in a quick add modal, we need to preprocess the HTML.

    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlText, 'text/html');
    const section = doc.getElementById(`MainProduct-${sectionId}`);

    if (this.isInQuickAddModal) {
      quickAddService.preprocessHTML(section, this.quickAddModal.modalContent);
    }

    if (section) this.productCache.set(handle, section);
    return section;
  }

  preloadSiblings() {
    this.inputs.forEach((input) => {
      const handle = input.value;
      if (!this.productCache.has(handle)) {
        this.getSectionForHandle(handle).catch(() => { });
      }
    });
  }

  preserveNonReplacable(currentSection, incomingSection) {
    try {
      const currentList = Array.from(
        currentSection.querySelectorAll('[data-sibling-product-switcher-non-replacable]')
      );
      if (!currentList.length) return;

      const incomingList = Array.from(
        incomingSection.querySelectorAll('[data-sibling-product-switcher-non-replacable]')
      );

      const count = Math.min(currentList.length, incomingList.length);
      for (let i = 0; i < count; i++) {
        const keepNode = currentList[i];
        const replaceTarget = incomingList[i];
        if (!keepNode || !replaceTarget) continue;
        replaceTarget.replaceWith(keepNode);
      }
    } catch (err) {
      console.warn('[SiblingProductSwitcher] preserveNonReplacable failed', err);
    }
  }
}

if (!customElements.get('product-mapped-products-switcher')) {
  customElements.define('product-mapped-products-switcher', ProductMappedProductsSwitcher);
}